// Per-user AI Assistant settings.
//
// Source of truth is the `ai_chat_settings` JSONB column on the active schema's
// `users` table. The legacy `aiAssistant_*` localStorage keys are kept as an
// instant cache + offline fallback (so the UI stays snappy and keeps working
// before the DB migration is applied). Both AIAssistant and MedStudentAIAssistant
// load/save through this module so behavior stays in lock-step.

// localStorage keys (the existing ones AIAssistant already wrote, plus one new).
const LS = {
  speedSetting: "aiAssistant_speedSetting",
  customMaxCharsPerDoc: "aiAssistant_customMaxCharsPerDoc",
  customMaxTotalLength: "aiAssistant_customMaxTotalLength",
  chatModel: "aiAssistant_chatModel",
  ocrModels: "aiAssistant_selectedModels", // existing key (JSON array)
  defaultSources: "aiAssistant_defaultSources", // new key (JSON array)
  activeSkill: "aiAssistant_activeSkill",
  skillIntensity: "aiAssistant_skillIntensity",
};

// Chat "skills" — system-prompt behavior modifiers a user can toggle on.
// Keep in sync with backend/api/skills/registry.py.
export const CAVEMAN_INTENSITIES = ["lite", "full", "ultra"];

// No pinned OCR models. An empty list means "newest model of each provider":
// the assistants fill the three priority slots from the live AgenticAI list
// once it loads (utils/chatModels.defaultOcrModelsFrom), and the backend does
// the same for a request that arrives with no modelIds.
export const DEFAULT_OCR_MODELS = [];

// defaultSources === null means "user hasn't chosen explicit defaults" → each
// assistant falls back to its own built-in default selection. An array (even
// empty) means an explicit choice and is honored as-is.
export const DEFAULT_AI_CHAT_SETTINGS = {
  speedSetting: "fast",
  customMaxCharsPerDoc: 5000,
  customMaxTotalLength: 50000,
  chatModel: 1,
  ocrModels: DEFAULT_OCR_MODELS,
  defaultSources: null,
  activeSkill: null, // null | 'caveman'
  skillIntensity: "full", // used only when activeSkill === 'caveman'
};

const SPEED_VALUES = ["fast", "medium", "slow", "advanced"];

// Badge text for an active skill — one source of truth so the chat header, the
// Process Sources modal, and the per-message chips never drift apart. Returns
// null when no skill is active, so callers can render nothing without a
// second "is it on?" check.
export function skillBadgeLabel(activeSkill, skillIntensity) {
  if (activeSkill !== "caveman") return null;
  const level = CAVEMAN_INTENSITIES.includes(skillIntensity)
    ? skillIntensity
    : DEFAULT_AI_CHAT_SETTINGS.skillIntensity;
  return `Caveman · ${level}`;
}

function toInt(value, fallback) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

// chatModel: a legacy numeric choice (0/1/2) or a real "claude-…" model id
// picked from the live list (see utils/chatModels.js).
function toChatModel(value, fallback) {
  if (typeof value === "string" && /^claude/i.test(value.trim())) {
    return value.trim();
  }
  return toInt(value, fallback);
}

// Accepts an array, a JSON-encoded array string, or junk → array | null.
function toArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || value === "") return null;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

// Coerce a partial / loosely-typed object into a complete settings object.
export function normalizeAiChatSettings(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    speedSetting: SPEED_VALUES.includes(r.speedSetting)
      ? r.speedSetting
      : DEFAULT_AI_CHAT_SETTINGS.speedSetting,
    customMaxCharsPerDoc: toInt(
      r.customMaxCharsPerDoc,
      DEFAULT_AI_CHAT_SETTINGS.customMaxCharsPerDoc,
    ),
    customMaxTotalLength: toInt(
      r.customMaxTotalLength,
      DEFAULT_AI_CHAT_SETTINGS.customMaxTotalLength,
    ),
    chatModel: toChatModel(r.chatModel, DEFAULT_AI_CHAT_SETTINGS.chatModel),
    ocrModels: toArray(r.ocrModels) || DEFAULT_OCR_MODELS,
    defaultSources: toArray(r.defaultSources), // null when absent
    activeSkill: r.activeSkill === "caveman" ? "caveman" : null,
    skillIntensity: CAVEMAN_INTENSITIES.includes(r.skillIntensity)
      ? r.skillIntensity
      : DEFAULT_AI_CHAT_SETTINGS.skillIntensity,
  };
}

export function getAiChatTruncationParameters(settings) {
  const s = normalizeAiChatSettings(settings);
  switch (s.speedSetting) {
    case "medium":
      return { maxCharsPerDocument: 10000, maxTotalContentLength: 100000 };
    case "slow":
      return { maxCharsPerDocument: 1000000, maxTotalContentLength: 1000000 };
    case "advanced":
      return {
        maxCharsPerDocument: s.customMaxCharsPerDoc,
        maxTotalContentLength: s.customMaxTotalLength,
      };
    case "fast":
    default:
      return { maxCharsPerDocument: 5000, maxTotalContentLength: 50000 };
  }
}

export function buildAiRequestSettings(settings, options = {}) {
  const s = normalizeAiChatSettings(settings);
  const modelIds = Array.isArray(s.ocrModels) ? s.ocrModels.filter(Boolean) : [];
  return {
    ...getAiChatTruncationParameters(s),
    modelId: modelIds.length ? modelIds[0] : null,
    modelIds,
    chatModel: options.chatModelAllowed ? s.chatModel : null,
    activeSkill: s.activeSkill,
    skillIntensity: s.skillIntensity,
  };
}

// Read whatever the legacy localStorage holds → a partial object ({} if none).
export function readLocalAiSettings() {
  try {
    const out = {};
    const pairs = [
      ["speedSetting", LS.speedSetting],
      ["customMaxCharsPerDoc", LS.customMaxCharsPerDoc],
      ["customMaxTotalLength", LS.customMaxTotalLength],
      ["chatModel", LS.chatModel],
      ["ocrModels", LS.ocrModels],
      ["defaultSources", LS.defaultSources],
      ["activeSkill", LS.activeSkill],
      ["skillIntensity", LS.skillIntensity],
    ];
    for (const [key, lsKey] of pairs) {
      const v = localStorage.getItem(lsKey);
      if (v != null) out[key] = v;
    }
    return out;
  } catch {
    return {};
  }
}

// True if localStorage held any legacy setting (drives the one-time DB backfill).
export function hasLocalAiSettings() {
  return Object.keys(readLocalAiSettings()).length > 0;
}

// Mirror a settings object into the legacy localStorage keys (instant cache).
export function writeLocalAiSettings(settings) {
  try {
    const s = normalizeAiChatSettings(settings);
    localStorage.setItem(LS.speedSetting, s.speedSetting);
    localStorage.setItem(LS.customMaxCharsPerDoc, String(s.customMaxCharsPerDoc));
    localStorage.setItem(LS.customMaxTotalLength, String(s.customMaxTotalLength));
    localStorage.setItem(LS.chatModel, String(s.chatModel));
    localStorage.setItem(LS.ocrModels, JSON.stringify(s.ocrModels));
    if (s.activeSkill != null) {
      localStorage.setItem(LS.activeSkill, s.activeSkill);
    } else {
      localStorage.removeItem(LS.activeSkill);
    }
    localStorage.setItem(LS.skillIntensity, s.skillIntensity);
    if (s.defaultSources != null) {
      localStorage.setItem(LS.defaultSources, JSON.stringify(s.defaultSources));
    } else {
      // Clear any stale explicit array so a later localStorage fallback doesn't
      // resurrect a choice the user has cleared back to "no preference".
      localStorage.removeItem(LS.defaultSources);
    }
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

// Persist settings to the DB (active schema users row) + localStorage cache.
// Returns { ok, error }. Never throws.
export async function saveAiChatSettings(supabase, userId, settings) {
  const normalized = normalizeAiChatSettings(settings);
  writeLocalAiSettings(normalized);
  if (!supabase || !userId) return { ok: false, error: "no-session" };
  try {
    const { error } = await supabase
      .from("users")
      .update({ ai_chat_settings: normalized })
      .eq("id", userId);
    if (error) return { ok: false, error };
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

// Load the user's settings. DB is source of truth; fall back to localStorage,
// then code defaults. One-time backfill: if the DB object is empty but
// localStorage has legacy settings, write them to the DB. Never throws.
export async function loadAiChatSettings(supabase, userId, options = {}) {
  const { mirrorToLocal = true, backfillToDb = true } = options || {};
  const local = normalizeAiChatSettings(readLocalAiSettings());
  if (!supabase || !userId) return local;
  try {
    const { data, error } = await supabase
      .from("users")
      .select("ai_chat_settings")
      .eq("id", userId)
      .maybeSingle();
    // Column missing (migration not run yet) / RLS / network → use local.
    if (error) return local;
    const dbObj = data?.ai_chat_settings;
    const dbHasKeys =
      dbObj && typeof dbObj === "object" && Object.keys(dbObj).length > 0;
    if (dbHasKeys) {
      const merged = normalizeAiChatSettings(dbObj);
      if (mirrorToLocal) {
        writeLocalAiSettings(merged);
      }
      return merged;
    }
    // DB empty — backfill from localStorage once if we have legacy settings.
    if (backfillToDb && hasLocalAiSettings()) {
      await saveAiChatSettings(supabase, userId, local).catch(() => {});
    }
    return local;
  } catch {
    return local;
  }
}
